import { KeyValue } from '@angular/common';
import { AppDate } from '@macs-frontend/core/utils/date';
import { AppDateTime } from '@macs-frontend/core/utils/datetime';
import { UUID } from '@macs-frontend/core/utils/uuid';
import { Province } from '@macs-frontend/features/shared/models/province';
import { Tag } from '@macs-frontend/features/shared/models/tag';
import { Boxed } from 'ngrx-forms';
import {
  ActionsCountByDay,
  ActionsCountByProject,
} from '../../company-management/planner/actions/planner-actions.store';
import { ActionConfirmationStatuses } from '../../public/action/action-confirmation/models/action-confirmation-statuses';
import {
  ActionPlanningDateAction,
  ActionPlanningItem,
  ATTransport,
  BookingPerson,
  MaterialGroupItem,
  Project,
  ProjectTeamMember,
} from '../../shared';
import { ActionsFormEntity } from './../../actions-form-shared/models/actions-form.model';
import { AbstractLocation } from './abstract-location';
import { ActionPlanningItemDate } from './action-planning';
import { ActionTeam } from './action-team';
import { BookingPersonStatus } from './booking-person';
import { Channel } from './channel.model';
import { BaseClient, ClientContact } from './client';
import { TeamCost } from '@macs-frontend/features/company-management/team-cost/team-cost.types';
import { BaseProject } from '@macs-frontend/features/shared/models/project';
import { BaseCompany } from '@macs-frontend/features/shared/models/company.model';
import { ActionFlowContext, ActionStatus } from '@macs-gql';

export interface ActionsListContainer {
  actions: MdAction[];
  bookingPersonPreview: BookingPersonsPreview;
  bookingPersonSearchPreview: BookingPersonSearchPreview;
  bookablePersonPreview: BookablePersonsPreview;
  actionsCountByDay: ActionsCountByDay[];
  actionsCountByProject: ActionsCountByProject[];
}

export interface BookingPersonSearchPreview {
  [actionId: string]: BookingPerson[];
}

export interface BookingPersonsPreview {
  [BookingPersonStatus.PENDING]: {
    [actionId: string]: BookingPerson[];
  };
  [BookingPersonStatus.BOOKED]: {
    [actionId: string]: BookingPerson[];
  };
}

export interface BookablePersonPreview {
  [actionId: string]: {
    data: BookingPerson[];
    total: number;
  };
}

export enum BookablePersonsPreviewTypes {
  VOLUNTEERED = 'VOLUNTEERED',
  REJECTED = 'REJECTED',
  REJECTED_TOO_LATE = 'REJECTED_TOO_LATE',
}

export type ProgressPercentages = 0 | 99 | 100;

export const progressPercentages: KeyValue<string, ProgressPercentages>[] = [
  {
    key: 'change to 0%',
    value: 0,
  },
  {
    key: 'change to 99%',
    value: 99,
  },
  {
    key: 'change to 100%',
    value: 100,
  },
];

export interface BookablePersonsPreview {
  [BookablePersonsPreviewTypes.VOLUNTEERED]: BookablePersonPreview;
  [BookablePersonsPreviewTypes.REJECTED]: BookablePersonPreview;
  [BookablePersonsPreviewTypes.REJECTED_TOO_LATE]: BookablePersonPreview;
}

export interface MdBaseAction {
  uuid: UUID;
  name: string;
  status: ActionStatus;
  confirmationStatus?: ActionConfirmationStatuses;
  confirmedBy?: string;
  code: string;
  warehouse: string;
  province: Province | null;
  estimated: number;
  projectedSamples: number | null;
  projectedContacts: number | null;
  transported: number;
  locationFeeCost: number;
  logo: string;
  resolvedLogo?: string;
  notes: string;
  scheduleRemark: string;
  // CP019: written by the client in the client portal, read-only here. It is
  // deliberately absent from ActionForm and MdActionPayload so an agency save
  // can never PATCH the client's own text back over itself.
  clientRemarks: string | null;
  actionType?: string;
  totalActionBillablePrice: number;
  bookingPersonsFullness: number;
  bookingPersonsQuantity: number;
  bookedBookingPersonsCount: number;
  pendingBookingPersonsCount: number;
  openBookingPersonsCount: number;
  planningStatus: ActionPlanningStatuses;
  firstScheduleStartTime: AppDateTime;
  lastScheduleEndTime: AppDateTime;
  createdAt?: AppDateTime;
  updatedAt?: AppDateTime;
  tankCode?: string;
  actionId?: string;
  canPrintContracts: boolean;
  progressPercentage?: ProgressPercentages;
  hasPickUpAction: boolean;
  hasDropOffAction: boolean;
  cancellationReason: string | null;
  hasOnlyDirectBookings?: boolean;
}

export interface MdActionTeam extends Omit<ActionTeam, 'action' | 'channel'> {
  channel: Omit<Channel, 'actionTeams'>;
}

export interface MdAction extends MdBaseAction {
  actionTeam: MdActionTeam;
  teamCost: TeamCost;
  parentTeamCost: null | TeamCost;
  disposableMaterial: MaterialGroupItem;
  transports: ATTransport[];
  actionPlanningDate: ActionPlanningDateAction;
  contactPersons: ClientContact[];
  locationContact: ClientContact;
  controller: ClientContact;
  projectLeader?: ProjectTeamMember;
  actionLocation?: ActionLocation;
  debriefing: ActionsFormEntity;
  companyForm: ActionsFormEntity;
  clientForm: ActionsFormEntity;
  projectClientForm: ActionsFormEntity;
  projectCompanyForm: ActionsFormEntity;
  tags: Tag[];
  project?: BaseProject & {
    client: BaseClient;
    briefing: Project['briefing'];
    company: BaseCompany;
  };
  flowContext: ActionFlowContext | null;
}

export enum ActionPlanningStatuses {
  NO_LOCATION = 'NO_LOCATION',
  OPEN = 'OPEN',
  READY = 'READY',
  PASSED = 'PASSED',
}

export interface ActionForm {
  name: string;
  contactPersons: Boxed<string[]>;
  locationContact: Boxed<ClientContact | null>;
  controller: string;
  warehouse: string;
  province: Province | null;
  estimated: number;
  projectedSamples: number | null;
  projectedContacts: number | null;
  transported: number;
  locationFeeCost: number;
  logo: string;
  notes: string;
  scheduleRemark: string;
  projectLeader?: string;
  actionLocation?: Boxed<ActionLocation>;
  disposableMaterial: string;
  totalActionBillablePrice: number;
  date: null | AppDate;
}

export interface MdActionPayload {
  uuid: UUID;
  name: string;
  status: string;
  actionTeam: UUID;
  contactPerson: string;
  locationContact: UUID | null;
  controller: string;
  actionLocation?: ActionLocation;
  code: string;
  actionPlanningDate: {
    date: null | AppDate;
    actionPlanningItem: string;
  };
  warehouse: string;
  province: Province | null;
  estimated: number;
  transported: number;
  locationFeeCost: number;
  logo: string;
  notes: string;
  scheduleRemark: string;
  projectLeader: string;
  disposableMaterial: string;
  totalActionBillablePrice: number;
  progressPercentage?: ProgressPercentages;
  tags: UUID[];
}

export interface ActionLocation extends AbstractLocation {
  /**
   * Province derived from the address' postal code, computed by the backend.
   * Advisory: it never overwrites the action's own province.
   */
  province?: Province | null;
}

export interface ActionFilterItem {
  uuid: UUID;
  label: string;
}

export interface RemoveActionEvent {
  actionPlanningItem: ActionPlanningItem;
  action: ActionPlanningItemDate;
}
